import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, Timestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Türkçe karakterleri koruyarak metni uppercase'e çevirir
 * @param {string} text - Çevrilecek metin
 * @returns {string} Uppercase'e çevrilmiş metin
 */
const toTurkishUpperCase = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Türkçe locale ile uppercase'e çevir (İ, Ş, Ğ, Ü, Ö, Ç karakterlerini doğru çevirir)
  try {
    return text.toLocaleUpperCase('tr-TR');
  } catch (error) {
    // Fallback: Eğer locale desteklenmiyorsa, manuel çeviri yap
    // Önce Türkçe karakterleri büyük harfe çevir, sonra diğer karakterleri
    return text
      .replace(/ı/g, 'I')
      .replace(/i/g, 'İ')
      .replace(/ş/g, 'Ş')
      .replace(/ğ/g, 'Ğ')
      .replace(/ü/g, 'Ü')
      .replace(/ö/g, 'Ö')
      .replace(/ç/g, 'Ç')
      .toUpperCase();
  }
};

/**
 * Yeni sertifika ekler
 * @param {Object} certificateData - Sertifika verileri
 * @returns {Promise<string>} Eklenen sertifikanın document ID'si
 */
export const addCertificateToFirestore = async (certificateData) => {
  try {
    // Auth kontrolü
    const { auth } = await import('../firebase/config');
    if (!auth || !auth.currentUser) {
      throw new Error('User is not authenticated. Please login first.');
    }
    console.log('Current user:', auth.currentUser.email);
    
    // Tarihleri Timestamp'e çevir
    const auditDate = certificateData.auditDate 
      ? Timestamp.fromDate(new Date(certificateData.auditDate))
      : null;
    
    const issueDate = certificateData.issueDate
      ? Timestamp.fromDate(new Date(certificateData.issueDate))
      : null;
    
    const expiryDate = certificateData.expiryDate
      ? Timestamp.fromDate(new Date(certificateData.expiryDate))
      : null;

    // Standards array'i temizle ve uppercase'e çevir
    const standards = Array.isArray(certificateData.standards)
      ? certificateData.standards
          .filter(s => s.trim().length > 0)
          .map(s => toTurkishUpperCase(s.trim()))
      : certificateData.standards
        ? certificateData.standards
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .map(s => toTurkishUpperCase(s))
        : [];

    // Tüm text alanlarını Türkçe karakterleri koruyarak uppercase'e çevir
    const data = {
      certificateNumber: toTurkishUpperCase(certificateData.certificateNumber.trim()),
      companyName: toTurkishUpperCase(certificateData.companyName.trim()),
      companyAddress: toTurkishUpperCase(certificateData.companyAddress.trim()),
      standards: standards,
      auditDate: auditDate,
      issueDate: issueDate,
      expiryDate: expiryDate,
      scope: toTurkishUpperCase(certificateData.scope.trim())
    };

    const docRef = await addDoc(collection(db, 'certificates'), data);
    return docRef.id;
  } catch (error) {
    console.error('Error adding certificate:', error);
    throw error;
  }
};

/**
 * Tüm sertifikaları getirir
 * @returns {Promise<Array>} Sertifika listesi
 */
export const getAllCertificates = async () => {
  try {
    const { auth } = await import('../firebase/config');
    if (!auth || !auth.currentUser) {
      throw new Error('User is not authenticated. Please login first.');
    }

    const certificatesRef = collection(db, 'certificates');
    const q = query(certificatesRef, orderBy('issueDate', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const certificates = [];
    querySnapshot.forEach((doc) => {
      certificates.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return certificates;
  } catch (error) {
    console.error('Error getting certificates:', error);
    throw error;
  }
};

/**
 * Sertifika günceller
 * @param {string} certificateId - Güncellenecek sertifikanın ID'si
 * @param {Object} certificateData - Güncellenecek sertifika verileri
 * @returns {Promise<void>}
 */
export const updateCertificate = async (certificateId, certificateData) => {
  try {
    const { auth } = await import('../firebase/config');
    if (!auth || !auth.currentUser) {
      throw new Error('User is not authenticated. Please login first.');
    }

    // Tarihleri Timestamp'e çevir
    const auditDate = certificateData.auditDate 
      ? Timestamp.fromDate(new Date(certificateData.auditDate))
      : null;
    
    const issueDate = certificateData.issueDate
      ? Timestamp.fromDate(new Date(certificateData.issueDate))
      : null;
    
    const expiryDate = certificateData.expiryDate
      ? Timestamp.fromDate(new Date(certificateData.expiryDate))
      : null;

    // Standards array'i temizle ve uppercase'e çevir
    const standards = Array.isArray(certificateData.standards)
      ? certificateData.standards
          .filter(s => s.trim().length > 0)
          .map(s => toTurkishUpperCase(s.trim()))
      : certificateData.standards
        ? certificateData.standards
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .map(s => toTurkishUpperCase(s))
        : [];

    // Tüm text alanlarını Türkçe karakterleri koruyarak uppercase'e çevir
    const data = {
      certificateNumber: toTurkishUpperCase(certificateData.certificateNumber.trim()),
      companyName: toTurkishUpperCase(certificateData.companyName.trim()),
      companyAddress: toTurkishUpperCase(certificateData.companyAddress.trim()),
      standards: standards,
      auditDate: auditDate,
      issueDate: issueDate,
      expiryDate: expiryDate,
      scope: toTurkishUpperCase(certificateData.scope.trim())
    };

    const certificateRef = doc(db, 'certificates', certificateId);
    await updateDoc(certificateRef, data);
  } catch (error) {
    console.error('Error updating certificate:', error);
    throw error;
  }
};

/**
 * Sertifika siler
 * @param {string} certificateId - Silinecek sertifikanın ID'si
 * @returns {Promise<void>}
 */
export const deleteCertificate = async (certificateId) => {
  try {
    const { auth } = await import('../firebase/config');
    if (!auth || !auth.currentUser) {
      throw new Error('User is not authenticated. Please login first.');
    }

    const certificateRef = doc(db, 'certificates', certificateId);
    await deleteDoc(certificateRef);
  } catch (error) {
    console.error('Error deleting certificate:', error);
    throw error;
  }
};

